/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Input, Form, Label as BaseLabel } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import ProductType from './ProductType';
import ManufacturerSelect from '../../containers/form/ManufacturerSelect';
import styles from '../../../../styles';

import { isMongoId } from '../../../../lib/formValidation';
import { priceIsNotValid, isNotNumber } from '../../../../lib/numberFormatter';

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const intlMessages = defineMessages({
    productNameError: {
        id: 'pages.inventory.AddProductDialog.productNameError',
        defaultMessage: 'Enter product name',
    },
    manufacturerError: {
        id: 'pages.inventory.AddProductDialog.manufacturerError',
        defaultMessage: 'Enter product manufacturer',
    },
    quantityError: {
        id: 'pages.inventory.AddProductDialog.quantityError',
        defaultMessage: 'Enter number of available items',
    },
    productPriceError: {
        id: 'pages.inventory.AddProductDialog.productPriceError',
        defaultMessage: 'Enter price the product sales for',
    },
    productTypeError: {
        id: 'pages.inventory.AddProductDialog.productTypeError',
        defaultMessage: 'Choice one of product types',
    },
    supplierPriceError: {
        id: 'pages.inventory.AddProductDialog.supplierPriceError',
        defaultMessage: 'Price contains invalid characters',
    },
});

function ProductForm({ intl }) {
    const { control, formState: { errors } } = useFormContext();

    return (
        <Form>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.name}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.name.label"
                            defaultMessage="Name"
                        />
                    </Label>
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.productNameError) }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.name && (
                        <BaseLabel color="red" pointing basic>{errors.name.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.manufacturer}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.manufacturer.label"
                            defaultMessage="Manufacturer"
                        />
                    </Label>
                    <Controller
                        name="manufacturer"
                        control={control}
                        rules={{
                            validate: value =>
                                !isMongoId(value)
                                    ? intl.formatMessage(intlMessages.manufacturerError)
                                    : true,
                        }}
                        render={({ field: { ref: _ref, value, onChange } }) => (
                            <ManufacturerSelect
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                            />
                        )}
                    />
                    {errors.manufacturer && (
                        <BaseLabel color="red" pointing basic>{errors.manufacturer.message}</BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.price}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.price.label"
                            defaultMessage="Price"
                        />
                    </Label>
                    <Controller
                        name="price"
                        control={control}
                        rules={{
                            validate: value =>
                                value == null || priceIsNotValid(value)
                                    ? intl.formatMessage(intlMessages.productPriceError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.price && (
                        <BaseLabel color="red" pointing basic>{errors.price.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.quantity}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.quantity.label"
                            defaultMessage="Quantity"
                        />
                    </Label>
                    <Controller
                        name="quantity"
                        control={control}
                        rules={{
                            validate: value =>
                                value == null || isNotNumber(value.toString())
                                    ? intl.formatMessage(intlMessages.quantityError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.quantity && (
                        <BaseLabel color="red" pointing basic>{errors.quantity.message}</BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.supplierPrice}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.supplierPrice.label"
                            defaultMessage="Supplier Price"
                        />
                    </Label>
                    <Controller
                        name="supplierPrice"
                        control={control}
                        rules={{
                            validate: value =>
                                value && value.length && priceIsNotValid(value)
                                    ? intl.formatMessage(intlMessages.supplierPriceError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.supplierPrice && (
                        <BaseLabel color="red" pointing basic>{errors.supplierPrice.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.code.label"
                            defaultMessage="Product Code"
                        />
                    </Label>
                    <Controller
                        name="code"
                        control={control}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Field error={!!errors.type}>
                <Label>
                    <FormattedMessage
                        id="pages.inventory.form.ProductForm.type.label"
                        defaultMessage="Type"
                    />
                </Label>
                <Controller
                    name="type"
                    control={control}
                    rules={{ required: intl.formatMessage(intlMessages.productTypeError) }}
                    render={({ field: { value, onChange } }) => (
                        <ProductType
                            value={value}
                            onChange={(e, data) => onChange(data?.value ?? e)}
                        />
                    )}
                />
                {errors.type && (
                    <BaseLabel color="red" pointing basic>{errors.type.message}</BaseLabel>
                )}
            </Form.Field>
        </Form>
    );
}

export default injectIntl(ProductForm);
